import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '@/components/Icon';
import { useSession } from '@/stores/session';
import { APPEARANCE_PRESETS, useAppearance } from '@/stores/appearance';
import { AppearanceEditor } from '@/features/common/AppearancePage';
import { logoutCurrentDevice } from './logout';
import s from './AvatarPopover.module.css';

const ROLE_NAMES: Record<string, string> = {
  ADMIN: '管理员', SALES: '业务员', FOLLOWER: '跟单员',
  WAREHOUSE: '仓库员', PURCHASE: '采购员', VIEWER: '查看员',
};

export function AvatarPopover() {
  const user = useSession((st) => st.user);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const appearance = useAppearance((st) => st.value);
  const appearanceName = APPEARANCE_PRESETS.find((preset) => preset.value.fontSize === appearance.fontSize && preset.value.lineHeight === appearance.lineHeight && preset.value.gap === appearance.gap)?.label || '自定义';
  const scope = user?.permissions.includes('orders.read.all') ? '全部数据'
    : user?.permissions.includes('orders.read.own') ? '本人创建'
      : user?.permissions.includes('orders.read.assigned') ? '分配给我' : '按角色授权';
  const role = user?.roleName || ROLE_NAMES[user?.role || ''] || user?.role || '—';

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) { setOpen(false); setConfirm(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy]);

  const logout = async () => {
    setBusy(true);
    setError('');
    try {
      await logoutCurrentDevice();
      setOpen(false);
      nav('/', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '退出失败，请重试。');
    } finally { setBusy(false); }
  };

  return <>
    <button type="button" className={s.avatarButton} title="我的账号" aria-label="我的账号菜单" aria-expanded={open} onClick={() => { setOpen((v) => !v); setConfirm(false); setError(''); }}>
      <b>{user?.name?.slice(0, 1) || '我'}</b>
    </button>
    {open && <>
      <div className={s.backdrop} onClick={() => !busy && setOpen(false)} />
      <div className={s.panel} role="dialog" aria-label="我的账号菜单">
        {confirm ? <div className={s.confirm}>
          <h2>退出登录？</h2>
          <p>这台设备将停止接收{user?.name || '当前账号'}的推送；本机外观偏好会保留。</p>
          {error && <p className={s.error} role="alert">{error}</p>}
          <div><button type="button" disabled={busy} onClick={() => setConfirm(false)}>取消</button><button type="button" className={s.danger} disabled={busy} onClick={() => void logout()}>{busy ? '正在退出…' : '退出登录'}</button></div>
        </div> : <>
          <button type="button" className={s.identity} onClick={() => { setOpen(false); nav('/me'); }}>
            <span>{user?.name?.slice(0, 1) || '我'}</span>
            <div><b>{user?.name || '当前账号'}<em>{role}</em></b><small>{user?.username || '—'} · 数据范围：{scope}</small></div>
          </button>
          <div className={s.sep} />
          <button type="button" className={s.item} onClick={() => { setOpen(false); setAppearanceOpen(true); }}><Icon name="type" />外观与密度<span>{appearanceName}</span></button>
          <button type="button" className={s.item} onClick={() => { setOpen(false); nav('/me/notifications'); }}><Icon name="bell" />通知与声音</button>
          <button type="button" className={s.item} onClick={() => { setOpen(false); nav('/me/password'); }}><Icon name="key" />修改密码</button>
          <button type="button" className={s.item} onClick={() => { setOpen(false); window.dispatchEvent(new Event('ims:v2:keyboard-shortcuts')); }}><Icon name="kbd" />键盘快捷键<span>?</span></button>
          <button type="button" className={s.item} onClick={() => { setOpen(false); nav('/me/install'); }}><Icon name="home" />添加到主屏幕</button>
          <button type="button" className={s.item} onClick={() => { setOpen(false); nav('/me/about'); }}><Icon name="info" />关于<span>v2.0.0</span></button>
          <div className={s.sep} />
          <button type="button" className={`${s.item} ${s.danger}`} onClick={() => setConfirm(true)}><Icon name="logout" />退出登录</button>
        </>}
      </div>
    </>}
    {appearanceOpen && <><div className={s.modalBackdrop} onClick={() => setAppearanceOpen(false)} /><AppearanceEditor sheet onClose={() => setAppearanceOpen(false)} /></>}
  </>;
}
